import { PLOTLY_THEME_DEFAULTS, PLOTLY_THEME_DEFAULTS_DARK, AXIS_LAYOUT_KEY_PATTERN } from "./constants"

export type ColorScheme = "light" | "dark"

type PlainObject = Record<string, unknown>

function isPlainObject(value: unknown): value is PlainObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function deepMerge(base: unknown, overrides: unknown): unknown {
  if (!isPlainObject(base)) {
    return overrides
  }

  if (!isPlainObject(overrides)) {
    return base
  }

  const merged: PlainObject = { ...base }

  Object.keys(overrides).forEach((key) => {
    const overrideValue = overrides[key]
    const baseValue = merged[key]
    merged[key] = isPlainObject(baseValue) && isPlainObject(overrideValue)
      ? deepMerge(baseValue, overrideValue)
      : overrideValue
  })

  return merged
}

function normalizePlotlyTitle(title: unknown): unknown {
  if (typeof title === "string") {
    return { text: title }
  }

  return title
}

function normalizePlotlyLayout(layout: unknown): PlainObject {
  const sourceLayout = isPlainObject(layout) ? layout : {}

  const normalizedLayout: PlainObject = { ...sourceLayout }
  if (sourceLayout.title !== undefined) {
    normalizedLayout.title = normalizePlotlyTitle(sourceLayout.title)
  }

  Object.keys(sourceLayout).forEach((key) => {
    const axisLayout = sourceLayout[key]
    if (!AXIS_LAYOUT_KEY_PATTERN.test(key) || !isPlainObject(axisLayout)) {
      return
    }

    const normalizedAxis: PlainObject = { ...axisLayout }
    if (axisLayout.title !== undefined) {
      normalizedAxis.title = normalizePlotlyTitle(axisLayout.title)
    }
    normalizedLayout[key] = normalizedAxis
  })

  return normalizedLayout
}

function createAxisTheme(textColor: string, secondaryTextColor: string, dividerColor: string): PlainObject {
  return {
    tickfont: { color: textColor },
    title: { font: { color: textColor } },
    gridcolor: dividerColor,
    zerolinecolor: secondaryTextColor,
    linecolor: dividerColor,
  }
}

export interface PlotlyLayoutColors {
  textColor?: string
  secondaryTextColor?: string
  dividerColor?: string
  paperColor?: string
}

interface SchemePalette {
  text?: { primary?: string, secondary?: string }
  divider?: string
  background?: { paper?: string }
}

interface ThemeWithColorSchemes {
  colorSchemes?: Partial<Record<string, { palette?: SchemePalette } | undefined>>
}

// The active scheme's RESOLVED palette (theme.colorSchemes[scheme].palette holds real color strings in CSS-vars
// mode — theme.vars.palette.* are the var refs, theme.palette.* is frozen to the default scheme). Reading it is
// synchronous in the same render as a useColorScheme toggle, so the re-plot never races a DOM CSS-var recompute.
function resolveThemeColors(theme: ThemeWithColorSchemes | null | undefined, scheme: ColorScheme): PlotlyLayoutColors | null {
  const palette = theme?.colorSchemes?.[scheme]?.palette
  if (!palette) return null
  return {
    textColor: palette.text?.primary,
    secondaryTextColor: palette.text?.secondary,
    dividerColor: palette.divider,
    paperColor: palette.background?.paper,
  }
}

// Chart chrome for the active scheme. `colors` are the resolved theme palette; `scheme` picks the no-palette
// fallback (tests / no provider). title.automargin reserves the title's own space so the legend can't wedge behind it.
function createThemeLayout(
  colors?: PlotlyLayoutColors | null,
  scheme?: ColorScheme | null,
): PlainObject {
  const fallback = scheme === "dark" ? PLOTLY_THEME_DEFAULTS_DARK : PLOTLY_THEME_DEFAULTS

  const textColor = colors?.textColor ?? fallback.textColor
  const secondaryTextColor = colors?.secondaryTextColor ?? fallback.textSecondaryColor
  const dividerColor = colors?.dividerColor ?? fallback.dividerColor
  const paperColor = colors?.paperColor ?? fallback.bgColor

  return {
    paper_bgcolor: paperColor,
    plot_bgcolor: paperColor,
    font: { color: textColor },
    // automargin keeps a top-anchored legend from floating above/into the title (esp. when maximized). yref
    // "paper" is required for automargin to expand the margin away from the CONTAINER edge — with the default
    // yref "container" the margin only clears the plot area, so the title still clips the top. (marginTop is the floor.)
    title: { font: { color: textColor }, automargin: true, yref: "paper" },
    xaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    yaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    // Pin the legend to the right of the plot (vertical). The docked panel is height-constrained but not
    // width-constrained, so a top/bottom legend fights the title/x-axis for scarce vertical room; the right
    // uses horizontal space and never overlaps the title at any height. Outside placement auto-expands the margin.
    legend: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
      orientation: "v",
      xanchor: "left",
      x: 1.02,
      yanchor: "top",
      y: 1,
    },
    modebar: {
      bgcolor: paperColor,
      color: secondaryTextColor,
      activecolor: textColor,
    },
    hoverlabel: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
    },
  }
}

const PRIMARY_AXIS_KEYS = new Set(["xaxis", "yaxis"])

function applyAxisThemeToLayout(layout: unknown, axisTheme: unknown): unknown {
  if (!isPlainObject(layout)) {
    return layout
  }

  const theme = isPlainObject(axisTheme) ? axisTheme : null
  if (!theme) {
    return layout
  }

  const nextLayout: PlainObject = { ...layout }

  Object.keys(layout).forEach((key) => {
    const axisLayout = layout[key]
    const isAxisKey = AXIS_LAYOUT_KEY_PATTERN.test(key) && isPlainObject(axisLayout)
    const isExtraAxis = isAxisKey && !PRIMARY_AXIS_KEYS.has(key)
    if (isExtraAxis) {
      // Themed chrome (gridcolor/tickfont/…) wins over the incoming axis; its data keys (range/type/…) survive.
      nextLayout[key] = deepMerge(axisLayout, theme)
    }
  })

  return nextLayout
}

export {
  applyAxisThemeToLayout,
  createThemeLayout,
  deepMerge,
  normalizePlotlyLayout,
  resolveThemeColors,
}
